// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { ImprHeaders } from '@imprivata-cloud/common';
import type { CodingContext } from '@imprivata-cloud/dataprivacy';
import { ClientHeader, Endpoints } from './constants';
import { FileAccessModifier } from './types';
import type {
  AppConfigGetResponse,
  AppConfigUpdateRequest,
  AppIntegrationFindByNameResponse,
  AppProfilesDeleteRequest,
  AppProfilesGetResponse,
  AppProfilesImportRequest,
  PrepareFileUploadResponse,
  IdvConnectionCreateRequest,
  IdvConnectionCreateResponse,
  IdvConnectionDeleteRequest,
  IdvConnectionDeleteResponse,
  IdvConnectionGetRequest,
  IdvConnectionGetResponse,
  IdvConnectionsFindResponse,
  IdvConnectionUpdateRequest,
  IdvConnectionUpdateResponse,
  AuthenticationStrengthGetRequest,
  AuthenticationStrengthGetResponse,
  AuthenticatorsFindByUserIdsRequest,
  AuthenticatorsFindByUserIdsResponse,
  AuthenticatorsUnenrollRequest,
  CredentialDeleteRequest,
  CredentialsFindRequest,
  CredentialsFindResponse,
  CustomerAgreementInfoGetResponse,
  DirectoriesFailedSyncFindResponse,
  DirectoryFailedSyncFindRequest,
  DirectorySyncProgressGetRequest,
  DirectorySyncProgressGetResponse,
  DocumentPurpose,
  EntraIdAuthenticationTestResponse,
  EntraIdConsentUrlGetRequest,
  EntraIdConsentUrlGetResponse,
  EntraIdTenantSetupResponse,
  EpcsAuditReportGetRequest,
  FactorLockoutClearRequest,
  FindDirectoriesResponse,
  FindListRequest,
  FindUsersRequest,
  FindUsersResponse,
  GetClientUserSessionResponse,
  GroupsFindResponse,
  IdpConfigGetRequest,
  IdpConfigGetResponse,
  IdpConfigUpdateRequest,
  IdpConfigUpdateResponse,
  IdpGetUserRequest,
  IdpGetUserResponse,
  IdpMetadataSaveRequest,
  ImportFileSubmitRequest,
  ImprIdpUsersFindRequest,
  ImprIdpUsersFindResponse,
  ImprUserDeleteRequest,
  ImprUserSaveRequest,
  ImprUserSaveResponse,
  LoginActivityReportGetRequest,
  MetadataSaveResponse,
  OrgPreferencesGetResponse,
  OrgPreferencesUpdateRequest,
  PasswordExpirationInfoGetResponse,
  PasswordPolicyGetResponse,
  PasswordPolicyUpdateRequest,
  PrepareUploadRespone,
  ProxCardMaxNumberGetResponse,
  ProxCardMaxNumberUpdateRequest,
  SettingsGetRequest,
  SettingsGetResponse,
  SettingsUpdateRequest,
  TempCodeAssignRequest,
  TempCodeAssignResponse,
  TemporaryCodeMetadataGetRequest,
  TemporaryCodeMetadataGetResponse,
  TenantInfoResponse,
  TenantLookupResponse,
  UsersInviteRequest,
  UsersInviteResponse,
  UserUnlockRequest,
  OidcDiscoveryResponse,
  EndpointDevicesFindRequest,
  EndpointDevicesFindResponse,
  EndpointInstallerData,
  EndpointInstallationKey,
  ConfigGetRequest,
  ConfigGetResponse,
  CustomSettingsChangeRequest,
  GracePeriodGetRequest,
  GracePeriodGetResponse,
  GracePeriodUpdateRequest,
} from './types';
import { createCodingCtxV2, fetcher, fetcherRaw, getApiUrl } from './utils';

const lookupTenant = async (
  userEmail: string,
  workflowId?: string,
): Promise<TenantLookupResponse> => {
  return fetcher(Endpoints.TENANT_LOOKUP_BY_DOMAIN, {
    method: 'POST',
    body: JSON.stringify({ userEmail }),
    workflowId,
  });
};

const getClientUserSession = async (
  workflowId: string,
  tenantId?: string,
): Promise<GetClientUserSessionResponse> => {
  return fetcher(Endpoints.GET_CLIENT_USER_SESSION, { method: 'POST', workflowId }, tenantId);
};

const getLandingPage = async (workflowId: string, tenantId: string) => {
  return fetcher(Endpoints.GET_LANDING_PAGE, { method: 'POST', workflowId }, tenantId);
};

const getIpRanges = async (tenantId: string): Promise<string[]> => {
  try {
    const response = await fetcher(Endpoints.IP_RANGES, { method: 'POST' }, tenantId);

    return response.cidrs || [];
  } catch (error) {
    console.error('Error fetching IP ranges:', error);
    return [];
  }
};

const findDirectories = async (tenantId: string): Promise<FindDirectoriesResponse> => {
  return fetcher(Endpoints.DIR_FIND, { method: 'POST' }, tenantId);
};

const findUsers = async (
  tenantId: string,
  request: FindUsersRequest,
): Promise<FindUsersResponse> => {
  return fetcher(Endpoints.USERS_FIND, { method: 'POST', body: JSON.stringify(request) }, tenantId);
};

async function getIdpConfigSAML(
  data: IdpConfigGetRequest,
  tenantId: string,
): Promise<IdpConfigGetResponse> {
  return fetcher(
    Endpoints.GET_IDP_CONFIG_SAML,
    { method: 'POST', body: JSON.stringify(data) },
    tenantId,
  );
}

export async function updateIdpConfigSAML(
  data: IdpConfigUpdateRequest,
  tenantId: string,
): Promise<IdpConfigUpdateResponse> {
  return fetcher(
    Endpoints.SAVE_AND_UPDATE_IDP_CONFIG_SAML,
    { method: 'POST', body: JSON.stringify(data) },
    tenantId,
  );
}

const getEntraIdConsentUrl = async (
  request: EntraIdConsentUrlGetRequest,
  tenantId: string,
): Promise<EntraIdConsentUrlGetResponse> => {
  return fetcher(
    Endpoints.ENTRA_ID_CONSENT,
    { method: 'POST', body: JSON.stringify(request) },
    tenantId,
  );
};

export async function dirSyncProgress(
  tenantId: string,
  directoryId: string,
): Promise<DirectorySyncProgressGetResponse> {
  return fetcher(
    Endpoints.DIR_SYNC_PROGRESS,
    { method: 'POST', body: JSON.stringify({ directoryId } as DirectorySyncProgressGetRequest) },
    tenantId,
  );
}

export async function dirFailedSyncFind(
  tenantId: string,
  directoryId: string,
): Promise<DirectoriesFailedSyncFindResponse> {
  return fetcher(
    Endpoints.DIR_FAILED_SYNC_FIND,
    { method: 'POST', body: JSON.stringify({ directoryId } as DirectoryFailedSyncFindRequest) },
    tenantId,
  );
}

const completeEntraIdSetup = async (tenantId: string): Promise<EntraIdTenantSetupResponse> => {
  return fetcher(Endpoints.ENTRA_ID_SETUP_COMPLETE, { method: 'POST' }, tenantId);
};

const testEntraIdAuthentication = async (
  providedName: string,
  password: string,
  tenantId: string,
): Promise<EntraIdAuthenticationTestResponse> => {
  const codingContext = await createCodingCtxV2();
  const encryptedPassword = codingContext.encryptString(password);
  return fetcher(
    Endpoints.ENTRA_ID_AUTHN_TEST,
    {
      method: 'POST',
      headers: { [ImprHeaders.ImprCodingCtx]: codingContext.buildImprCodingCtxHeader() },
      body: JSON.stringify({ providedName, password: encryptedPassword }),
    },
    tenantId,
  );
};

const portalLogout = async (tenantId: string) => {
  console.log('portalLogout tenantId:', tenantId);
  return fetcher(Endpoints.LOGOUT, { method: 'GET', keepalive: true }, tenantId);
};

export function orgPreferencesGet(): Promise<OrgPreferencesGetResponse> {
  return fetcher(Endpoints.PORTAL_ORG_PREFERENCES_GET, { method: 'POST' });
}

export function orgPreferencesUpdate(data: OrgPreferencesUpdateRequest) {
  return fetcher(Endpoints.PORTAL_ORG_PREFERENCES_UPDATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function idpSave(data: IdpMetadataSaveRequest): Promise<MetadataSaveResponse> {
  return fetcher(Endpoints.IDP_SAVE, { method: 'POST', body: JSON.stringify(data) });
}

export async function credentialsFind(
  data: CredentialsFindRequest,
): Promise<CredentialsFindResponse> {
  return fetcher(Endpoints.PORTAL_CREDENTIALS_FIND, { method: 'POST', body: JSON.stringify(data) });
}

export async function credentialDelete(data: CredentialDeleteRequest): Promise<void> {
  return fetcher(Endpoints.PORTAL_CREDENTIALS_DELETE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

const findAuthenticatorsByUserIds = async (
  data: AuthenticatorsFindByUserIdsRequest,
): Promise<AuthenticatorsFindByUserIdsResponse> => {
  return fetcher(Endpoints.FIND_AUTHENTICATORS, { method: 'POST', body: JSON.stringify(data) });
};

const unenrollAuthenticators = async (
  data: AuthenticatorsUnenrollRequest,
  tenantId: string,
): Promise<void> => {
  return fetcher(
    Endpoints.UNENROLL_AUTHENTICATORS,
    { method: 'POST', body: JSON.stringify(data) },
    tenantId,
  );
};

const getTenantInfo = async (tenantId: string): Promise<TenantInfoResponse> => {
  return fetcher(Endpoints.TENANT_INFO, { method: 'POST' }, tenantId);
};

const findGroups = async (data: FindListRequest, tenantId: string): Promise<GroupsFindResponse> => {
  return fetcher(Endpoints.GROUPS_FIND, { method: 'POST', body: JSON.stringify(data) }, tenantId);
};

export const findImprIdpUsers = async (
  request: ImprIdpUsersFindRequest,
  tenantId: string,
): Promise<ImprIdpUsersFindResponse> => {
  return fetcher(
    Endpoints.PORTAL_IMPR_IDP_USERS_FIND,
    { method: 'POST', body: JSON.stringify(request) },
    tenantId,
  );
};

export async function imprIdpUsersSave(
  request: ImprUserSaveRequest,
): Promise<ImprUserSaveResponse> {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USERS_SAVE, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export const deleteImprIdpUser = async (payload: ImprUserDeleteRequest): Promise<void> => {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USERS_DELETE, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

async function getPrepareUploadUrl(
  filename: string,
  purpose: DocumentPurpose,
): Promise<PrepareUploadRespone> {
  return fetcher(Endpoints.LEGAL_AGREEMENTS_PREPARE, {
    method: 'POST',
    body: JSON.stringify({ filename, purpose }),
  });
}

async function completeLegalDocumentsUpload(
  readUrl: string,
  operationId: string,
): Promise<PrepareUploadRespone> {
  return fetcher(Endpoints.LEGAL_AGREEMENTS_COMPLETE, {
    method: 'POST',
    body: JSON.stringify({ readUrl, operationId }),
  });
}

async function getCustomerAgreementInfo(): Promise<CustomerAgreementInfoGetResponse> {
  return fetcher(Endpoints.LEGAL_AGREEMENTS_INFO_GET, { method: 'POST' });
}

export async function getUserDetails(request: IdpGetUserRequest): Promise<IdpGetUserResponse> {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USER_DETAILS_GET, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

const assignTempCode = async (
  data: TempCodeAssignRequest,
  codingContext: CodingContext,
): Promise<TempCodeAssignResponse> => {
  return fetcher(Endpoints.PORTAL_TEMP_CODE_ASSIGN, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Impr-Coding-Ctx': codingContext.buildImprCodingCtxHeader() },
  });
};

const getTemporaryCodeMetadata = async (
  data: TemporaryCodeMetadataGetRequest,
): Promise<TemporaryCodeMetadataGetResponse> => {
  return fetcher(Endpoints.PORTAL_TEMP_CODE_METADATA_GET, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export async function getConfigSettings(request: SettingsGetRequest): Promise<SettingsGetResponse> {
  return fetcher(Endpoints.PORTAL_CONFIG_SETTINGS_GET, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export async function updateConfigSettings(request: SettingsUpdateRequest): Promise<void> {
  return fetcher(Endpoints.PORTAL_CONFIG_SETTINGS_UPDATE, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export const unlockImprIdpUser = async (data: UserUnlockRequest): Promise<void> => {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USERS_UNLOCK, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const clearFactorLockouts = async (data: FactorLockoutClearRequest): Promise<void> => {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USERS_FACTOR_LOCKOUT_CLEAR, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const clearUserFactorLockouts = async (data: FactorLockoutClearRequest): Promise<void> => {
  return fetcher(Endpoints.PORTAL_USERS_FACTOR_LOCKOUT_CLEAR, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export async function inviteImprIdpUsers(data: UsersInviteRequest): Promise<UsersInviteResponse> {
  return fetcher(Endpoints.PORTAL_IMPR_IDP_USERS_INVITE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

const getUsersImportTemplate = async (tenantId: string): Promise<Blob> => {
  const response = await fetcherRaw(
    Endpoints.PORTAL_USERS_UPLOAD_TEMPLATE_GET,
    { method: 'GET' },
    tenantId,
  );
  return response.blob();
};

export async function getLoginActivityReport(data: LoginActivityReportGetRequest): Promise<Blob> {
  const response = await fetcherRaw(Endpoints.PORTAL_REPORTS_LOGIN_ACTIVITY_GET, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
  return response.blob();
}

export async function getEpcsAuditReport(data: EpcsAuditReportGetRequest): Promise<Blob> {
  const response = await fetcherRaw(Endpoints.PORTAL_REPORTS_EPCS_AUDIT_GET, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });
  return response.blob();
}

export async function getAgentVersionReport(): Promise<Blob> {
  const response = await fetcherRaw(Endpoints.PORTAL_REPORTS_AGENT_VERSION_GET, { method: 'POST' });
  return response.blob();
}

export async function getUserEnrollmentReport(): Promise<Blob> {
  const response = await fetcherRaw(Endpoints.PORTAL_REPORTS_USER_ENROLLMENT_GET, {
    method: 'POST',
  });
  return response.blob();
}

const submitUsersCsv = async (
  data: ImportFileSubmitRequest,
  tenantId?: string,
): Promise<Response> => {
  const formData = new FormData();
  // Create a new Blob with explicit text/csv MIME type to handle Windows assigning
  // application/vnd.ms-excel to CSV files
  const csvBlob = new Blob([data.importFile], { type: 'text/csv' });
  formData.append('importFile', csvBlob, data.importFile.name);
  return fetcherRaw(
    Endpoints.PORTAL_USERS_UPLOAD_SUBMIT,
    { method: 'POST', body: formData },
    tenantId,
  );
};

const getPasswordPolicy = async (): Promise<PasswordPolicyGetResponse> => {
  return fetcher(Endpoints.PASSWORD_POLICY_GET, { method: 'POST' });
};

const getAuthenticationStrength = async (
  request: AuthenticationStrengthGetRequest,
): Promise<AuthenticationStrengthGetResponse> => {
  return fetcher(Endpoints.AUTHENTICATION_STRENGTH_GET, {
    method: 'POST',
    body: JSON.stringify(request),
  });
};

export async function getGracePeriod(
  request: GracePeriodGetRequest,
  tenantId: string,
): Promise<GracePeriodGetResponse> {
  return fetcher(
    Endpoints.GRACE_PERIOD_GET,
    { method: 'POST', body: JSON.stringify(request), headers: adminWebClientNameHeader },
    tenantId,
  );
}

export async function updateGracePeriod(
  request: GracePeriodUpdateRequest,
  tenantId: string,
): Promise<void> {
  return fetcher(
    Endpoints.GRACE_PERIOD_UPDATE,
    { method: 'POST', body: JSON.stringify(request), headers: adminWebClientNameHeader },
    tenantId,
  );
}

const updatePasswordPolicy = async (
  data: PasswordPolicyUpdateRequest,
): Promise<Record<string, never>> => {
  return fetcher(Endpoints.PASSWORD_POLICY_UPDATE, { method: 'POST', body: JSON.stringify(data) });
};

const getPasswordExpirationInfo = async (): Promise<PasswordExpirationInfoGetResponse> => {
  return fetcher(Endpoints.PORTAL_PASSWORD_EXPIRATION, { method: 'POST' });
};

// The /admin-web/* endpoints reject requests that omit impr-client-name.
// Matches the inline encoding in src/api/utils.ts and the per-call header
// pattern in src/api/selfServiceServices.ts (ClientHeader.IDP_WEB).
const adminWebClientNameHeader = {
  [ImprHeaders.ImprClientName]: btoa(JSON.stringify({ name: ClientHeader.IDP_WEB })),
};

const findAppIntegrationByName = async (
  appName: string,
  tenantId: string,
): Promise<AppIntegrationFindByNameResponse> => {
  return fetcher(
    Endpoints.APP_INTEGRATION_FIND_BY_NAME,
    { method: 'POST', body: JSON.stringify({ appName }), headers: adminWebClientNameHeader },
    tenantId,
  );
};

const findAppConfig = async (
  applicationId: string,
  tenantId: string,
): Promise<AppConfigGetResponse> => {
  return fetcher(
    Endpoints.APP_CONFIG_FIND,
    { method: 'POST', body: JSON.stringify({ applicationId }), headers: adminWebClientNameHeader },
    tenantId,
  );
};

const updateAppConfig = async (
  data: AppConfigUpdateRequest,
  tenantId: string,
): Promise<Record<string, never>> => {
  return fetcher(
    Endpoints.APP_CONFIG_UPDATE,
    { method: 'POST', body: JSON.stringify(data), headers: adminWebClientNameHeader },
    tenantId,
  );
};

const getProxCardMaxNumber = async (): Promise<ProxCardMaxNumberGetResponse> => {
  return fetcher(Endpoints.PROX_CARD_MAX_NUMBER_GET, { method: 'POST' });
};

const updateProxCardMaxNumber = async (
  data: ProxCardMaxNumberUpdateRequest,
): Promise<Record<string, never>> => {
  return fetcher(Endpoints.PROX_CARD_MAX_NUMBER_UPDATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// IDV Connection APIs
export async function findIdvConnections(): Promise<IdvConnectionsFindResponse> {
  return fetcher(Endpoints.IDV_CONNECTIONS_FIND, {
    method: 'POST',
    body: JSON.stringify({})
  });
}

export async function getIdvConnection(
  data: IdvConnectionGetRequest,
): Promise<IdvConnectionGetResponse> {
  return fetcher(Endpoints.IDV_CONNECTION_GET, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function createIdvConnection(
  data: IdvConnectionCreateRequest,
  codingContext: CodingContext,
): Promise<IdvConnectionCreateResponse> {
  return fetcher(Endpoints.IDV_CONNECTIONS_CREATE, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { [ImprHeaders.ImprCodingCtx]: codingContext.buildImprCodingCtxHeader() },
  });
}

// Fetches the tenant's standard OIDC discovery document. The URL is derived per-tenant
// (`getApiUrl(tenantId)` yields e.g. `https://<tenant>.sys.<env>.imprivata.cloud`), never
// hardcoded. Drives both the authorization/token endpoint URLs and the Custom form's
// supported-scopes list.
//
// This is a PUBLIC, cross-origin metadata endpoint, so it deliberately does NOT go through
// `fetcher`: the discovery host differs from the app origin and its CORS response allows the
// origin but not credentials (no `Access-Control-Allow-Credentials`). `fetcher` sends
// `credentials: 'include'` and a JSON `Content-Type`, which the browser would block (a
// credentialed response without that header is discarded) and which forces an unnecessary
// preflight. A plain unauthenticated GET with no custom headers avoids both.
export async function fetchOidcDiscovery(tenantId: string): Promise<OidcDiscoveryResponse> {
  const url = `${getApiUrl(tenantId)}/.well-known/openid-configuration`;
  const response = await fetch(url, { method: 'GET', credentials: 'omit' });
  if (!response.ok) {
    return Promise.reject(new Error(`OIDC discovery failed: ${response.status}`));
  }
  return response.json();
}

export async function updateIdvConnection(
  data: IdvConnectionUpdateRequest,
): Promise<IdvConnectionUpdateResponse> {
  return fetcher(Endpoints.IDV_CONNECTIONS_UPDATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteIdvConnection(
  data: IdvConnectionDeleteRequest,
): Promise<IdvConnectionDeleteResponse> {
  return fetcher(Endpoints.IDV_CONNECTIONS_DELETE, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Endpoints management (LYRA-1744)
export async function findEndpointDevices(
  request: EndpointDevicesFindRequest,
): Promise<EndpointDevicesFindResponse> {
  return fetcher(Endpoints.ENDPOINT_DEVICES_FIND, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: adminWebClientNameHeader,
  });
}

export async function getEndpointInstallerData(): Promise<EndpointInstallerData> {
  return fetcher(Endpoints.ENDPOINT_INSTALLER_DATA_GET, {
    method: 'POST',
    headers: adminWebClientNameHeader,
  });
}

export async function generateEndpointInstallationKey(
  codingContext: CodingContext,
): Promise<EndpointInstallationKey> {
  return fetcher(Endpoints.ENDPOINT_INSTALLATION_KEY_GENERATE, {
    method: 'POST',
    headers: {
      ...adminWebClientNameHeader,
      [ImprHeaders.ImprCodingCtx]: codingContext.buildImprCodingCtxHeader(),
    },
  });
}

export async function getEndpointBehaviorConfig(
  request: ConfigGetRequest,
): Promise<ConfigGetResponse> {
  return fetcher(Endpoints.ENDPOINT_SETTINGS_CONFIG_GET, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: adminWebClientNameHeader,
  });
}

export async function changeEndpointCustomSettings(
  request: CustomSettingsChangeRequest,
): Promise<void> {
  return fetcher(Endpoints.ENDPOINT_SETTINGS_CUSTOM_CHANGE, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: adminWebClientNameHeader,
  });
}

// Application Profiles (admin-web-app-profiles v1)
export async function getAppProfiles(): Promise<AppProfilesGetResponse> {
  return fetcher(Endpoints.APP_PROFILES_GET, { method: 'POST', headers: adminWebClientNameHeader });
}

export async function importAppProfile(request: AppProfilesImportRequest): Promise<void> {
  return fetcher(Endpoints.APP_PROFILES_IMPORT, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: adminWebClientNameHeader,
  });
}

export async function deleteAppProfiles(request: AppProfilesDeleteRequest): Promise<void> {
  return fetcher(Endpoints.APP_PROFILES_DELETE, {
    method: 'POST',
    body: JSON.stringify(request),
    headers: adminWebClientNameHeader,
  });
}

// Import file-upload path (admin-web-file-access v2). Issues a `readUrl`
// that `astra-app-profile-svc`'s own BFF can fetch back, paired with the
// PRIVATE modifier since the file only needs to be readable by Astra cloud
// services, not the public internet.
export async function prepareAppProfileUpload(fileName: string): Promise<PrepareFileUploadResponse> {
  return fetcher(Endpoints.APP_PROFILE_PREPARE_UPLOAD, {
    method: 'POST',
    body: JSON.stringify({ fileAccessModifier: FileAccessModifier.PRIVATE, fileSuffix: fileName }),
    headers: adminWebClientNameHeader,
  });
}

export {
  lookupTenant,
  getClientUserSession,
  getEntraIdConsentUrl,
  getLandingPage,
  getIpRanges,
  portalLogout,
  completeEntraIdSetup,
  findDirectories,
  getIdpConfigSAML,
  findGroups,
  findUsers,
  findAuthenticatorsByUserIds,
  unenrollAuthenticators,
  getTenantInfo,
  getPrepareUploadUrl,
  completeLegalDocumentsUpload,
  getCustomerAgreementInfo,
  assignTempCode,
  getTemporaryCodeMetadata,
  getUsersImportTemplate,
  submitUsersCsv,
  getPasswordPolicy,
  updatePasswordPolicy,
  getPasswordExpirationInfo,
  getAuthenticationStrength,
  findAppIntegrationByName,
  findAppConfig,
  updateAppConfig,
  getProxCardMaxNumber,
  updateProxCardMaxNumber,
  testEntraIdAuthentication,
};
